import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { PhotoUploader, LazyPhotoGallery } from '../../components/PhotoComponents';
import { toast } from 'sonner';
import { Plus, AlertTriangle, Clock, Cog, CheckCircle, Image } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_OPTIONS = [
  { value: 'baja', label: 'Baja' },
  { value: 'media', label: 'Media' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' },
];

export default function ClientIncidents() {
  const [incidents, setIncidents] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    machine_id: '',
    title: '',
    description: '',
    priority: 'media',
    photos: []
  });

  const fetchData = useCallback(async () => {
    try {
      const [incidentsRes, machinesRes] = await Promise.all([
        axios.get(`${API}/incidents`),
        axios.get(`${API}/machines`)
      ]);
      setIncidents(incidentsRes.data);
      setMachines(machinesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description) {
      toast.error('Título y descripción son requeridos');
      return;
    }

    try {
      await axios.post(`${API}/incidents`, {
        ...formData,
        machine_id: formData.machine_id || null
      });
      toast.success('Incidencia reportada correctamente');
      setDialogOpen(false);
      setFormData({ machine_id: '', title: '', description: '', priority: 'media', photos: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear incidencia');
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      baja: 'text-slate-400',
      media: 'text-amber-400',
      alta: 'text-orange-400',
      urgente: 'text-rose-400',
    };
    return <span className={`font-mono text-xs uppercase ${colors[priority]}`}>{priority}</span>;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pendiente: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      en_proceso: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
      resuelto: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    };
    const labels = { pendiente: 'Pendiente', en_proceso: 'En Proceso', resuelto: 'Resuelto' };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-mono uppercase tracking-wider border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getStatusIcon = (status) => {
    const icons = {
      pendiente: <AlertTriangle className="w-5 h-5 text-amber-400" />,
      en_proceso: <Clock className="w-5 h-5 text-sky-400" />,
      resuelto: <CheckCircle className="w-5 h-5 text-emerald-400" />,
    };
    return icons[status];
  };

  return (
    <Layout title="Mis Incidencias">
      <div className="space-y-6 animate-fade-in" data-testid="client-incidents">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-slate-400">Reporta problemas y sigue el estado de tus incidencias</p>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sky-600 hover:bg-sky-500 text-white gap-2" data-testid="create-incident-btn">
                <Plus className="w-4 h-4" />
                Nueva Incidencia
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-rajdhani text-xl">Reportar Incidencia</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Máquina Afectada</Label>
                  <Select
                    value={formData.machine_id || undefined}
                    onValueChange={(v) => setFormData({ ...formData, machine_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="incident-machine-select">
                      <SelectValue placeholder="Selecciona (opcional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="none">General / Sin máquina específica</SelectItem>
                      {machines.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Título *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="Resumen breve del problema"
                    data-testid="incident-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Prioridad</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(v) => setFormData({ ...formData, priority: v })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="incident-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {PRIORITY_OPTIONS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Descripción *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white min-h-[120px]"
                    placeholder="Describe el problema con el mayor detalle posible..."
                    data-testid="incident-description-input"
                  />
                </div>
                
                {/* Photo Upload */}
                <div className="space-y-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Fotos del Problema
                  </Label>
                  <PhotoUploader
                    photos={formData.photos}
                    onChange={(photos) => setFormData({ ...formData, photos })}
                    maxPhotos={5}
                  />
                  <p className="text-xs text-slate-500">
                    Adjunta fotos para mostrar el problema visualmente
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-400">
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-sky-600 hover:bg-sky-500" data-testid="submit-incident-btn">
                    Enviar Incidencia
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Incidents List */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando...</div>
        ) : incidents.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-12 text-center">
              <AlertTriangle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No tienes incidencias registradas</p>
              <p className="text-sm text-slate-500 mt-1">¡Crea una si tienes algún problema!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {incidents.map((incident) => (
              <Card 
                key={incident.id} 
                className={`bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all ${
                  incident.priority === 'urgente' ? 'border-l-2 border-l-rose-500' : 
                  incident.priority === 'alta' ? 'border-l-2 border-l-orange-500' : ''
                }`}
                data-testid={`incident-${incident.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
                      {getStatusIcon(incident.status)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getStatusBadge(incident.status)}
                        {getPriorityBadge(incident.priority)}
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-1">{incident.title}</h3>
                      <p className="text-slate-400 mb-3">{incident.description}</p>
                      
                      {/* Photos */}
                      {incident.photo_count > 0 && (
                        <div className="mb-3">
                          <LazyPhotoGallery id={incident.id} type="incidents" count={incident.photo_count} />
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        {incident.machine_name && (
                          <span className="flex items-center gap-1">
                            <Cog className="w-4 h-4" />
                            {incident.machine_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1 font-mono">
                          <Clock className="w-4 h-4" />
                          {new Date(incident.created_at).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                      {incident.resolved_at && (
                        <div className="mt-3 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-sm">
                          <p className="text-xs text-emerald-400 font-medium">
                            Resuelto el {new Date(incident.resolved_at).toLocaleDateString('es-ES')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
